import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, LogOut, MessageCircle, ShoppingBag, User, Check } from "lucide-react";
import { customerMe, customerGetMessages, customerSendMessage, customerLogout, getCustomerToken } from "@/lib/api";
import { toast } from "sonner";

export default function AccountPage() {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState("messages");  // 'messages' | 'profile'
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!getCustomerToken()) {
      navigate("/", { replace: true });
      return;
    }
    customerMe()
      .then(setCustomer)
      .catch(() => { navigate("/", { replace: true }); })
      .finally(() => setLoading(false));
  }, [navigate]);

  const loadMessages = async () => {
    try {
      const r = await customerGetMessages();
      const incoming = r.items || [];
      const serverIds = new Set(incoming.map((m) => m.id));
      // Keep any locally-appended messages that the server hasn't acknowledged yet
      // (handles race where poll fires between optimistic append and DB commit).
      setMessages((prev) => {
        const localExtras = prev.filter((m) => !serverIds.has(m.id));
        return [...incoming, ...localExtras];
      });
      // Backend marks admin messages as read on this GET — tell Layout to refresh its badge.
      try { window.dispatchEvent(new Event("customer-messages-read")); } catch (_e) { /* ignore */ }
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 50);
    } catch (_) { /* keep silent — polling is best-effort */ }
  };

  // Poll messages every 5 s while on messages tab
  useEffect(() => {
    if (!customer || tab !== "messages") return;
    loadMessages();
    const id = setInterval(loadMessages, 5000);
    return () => clearInterval(id);
  }, [customer, tab]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text) return;
    setSending(true);
    try {
      const m = await customerSendMessage(text);
      // Deduplicate: poll may have already pulled the new message between request and response.
      setMessages((p) => p.some((x) => x.id === m.id) ? p : [...p, m]);
      setDraft("");
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 50);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ошибка отправки");
    } finally { setSending(false); }
  };

  const handleLogout = () => {
    customerLogout();
    navigate("/");
  };

  const formatTime = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" });
    } catch { return ""; }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-zinc-500 text-sm">Загрузка...</div>;
  }
  if (!customer) return null;

  const initial = (customer.display_name || customer.tg_first_name || customer.username || "?").slice(0, 1).toUpperCase();

  return (
    <div className="min-h-[80vh] max-w-3xl mx-auto px-4 py-6 lg:py-10" data-testid="account-page">
      {/* Header card */}
      <div className="flex items-start gap-4 pb-6 border-b border-zinc-800">
        <div className="w-14 h-14 rounded-full bg-zinc-800 flex items-center justify-center font-display font-bold text-xl shrink-0">
          {initial}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-xl tracking-wider truncate" data-testid="account-display-name">
            {customer.display_name || customer.tg_first_name || customer.username}
          </h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {customer.username && (
              <span className="text-[11px] font-mono text-zinc-500">@{customer.username}</span>
            )}
            {customer.has_telegram && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono text-emerald-400 px-1.5 py-0.5 border border-emerald-900/50">
                <Check className="w-2.5 h-2.5" /> Telegram
              </span>
            )}
            {customer.orders_count > 0 && (
              <span className="text-[11px] font-mono text-amber-400">⭐ {customer.orders_count} заказов</span>
            )}
          </div>
        </div>
        <button onClick={handleLogout} className="p-2 hover:bg-zinc-900 transition-colors text-zinc-500 hover:text-white" title="Выйти" data-testid="account-logout-btn">
          <LogOut className="w-4 h-4" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-zinc-800 -mx-4 px-4 mt-4 gap-1">
        <button onClick={() => setTab("messages")}
          className={`px-4 py-3 text-xs font-display font-bold uppercase tracking-wider flex items-center gap-1.5 border-b-2 transition-colors ${tab === "messages" ? "border-white text-white" : "border-transparent text-zinc-500 hover:text-white"}`}
          data-testid="account-tab-messages">
          <MessageCircle className="w-3.5 h-3.5" /> Сообщения
        </button>
        <button onClick={() => setTab("profile")}
          className={`px-4 py-3 text-xs font-display font-bold uppercase tracking-wider flex items-center gap-1.5 border-b-2 transition-colors ${tab === "profile" ? "border-white text-white" : "border-transparent text-zinc-500 hover:text-white"}`}
          data-testid="account-tab-profile">
          <User className="w-3.5 h-3.5" /> Профиль
        </button>
      </div>

      {tab === "messages" && (
        <div className="mt-4 flex flex-col h-[60vh]" data-testid="account-messages-pane">
          <div ref={scrollRef} className="flex-1 overflow-y-auto pr-1 py-3">
            {messages.length === 0 ? (
              <div className="h-full flex items-center justify-center text-center px-6">
                <div className="space-y-1">
                  <MessageCircle className="w-10 h-10 mx-auto text-zinc-700" strokeWidth={1.5} />
                  <p className="text-sm text-zinc-400">Здесь будет переписка с Amberglow</p>
                  <p className="text-[11px] font-mono text-zinc-600">Напишите нам — мы ответим в течение дня</p>
                </div>
              </div>
            ) : (
              messages.map((m, idx) => {
                const prev = messages[idx - 1];
                const newSender = !prev || prev.from !== m.from;
                return (
                  <div key={m.id} className={`flex ${m.from === "customer" ? "justify-end" : "justify-start"} ${newSender ? "mt-8 first:mt-0" : "mt-4"}`} data-testid={`msg-${m.id}`}>
                    <div className={`max-w-[85%] px-4 py-3 ${m.from === "customer" ? "bg-white text-black" : "bg-zinc-900 text-white border border-zinc-800"}`}>
                      <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{m.text}</p>
                      <p className={`text-[10px] font-mono mt-2 ${m.from === "customer" ? "text-zinc-500" : "text-zinc-600"}`}>
                        {formatTime(m.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div className="border-t border-zinc-800 pt-3 mt-3 flex gap-2">
            <textarea value={draft} onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Напишите сообщение..." rows={2}
              className="flex-1 bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm text-white focus:outline-none focus:border-zinc-600 resize-none"
              data-testid="account-msg-input" />
            <button onClick={handleSend} disabled={sending || !draft.trim()}
              className="h-auto px-4 bg-white text-black hover:bg-zinc-200 disabled:opacity-40 transition-colors flex items-center" data-testid="account-msg-send">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {tab === "profile" && (
        <div className="mt-4 space-y-4" data-testid="account-profile-pane">
          <Row label="Имя" value={customer.display_name || customer.tg_first_name || "—"} />
          {customer.username && <Row label="Логин" value={"@" + customer.username} />}
          {customer.tg_username && <Row label="Telegram" value={"@" + customer.tg_username} />}
          <Row label="Способ входа" value={customer.source === "site_register" ? "Сайт (логин/пароль)" : "Telegram"} />
          <Row label="Заказов оформлено" value={customer.orders_count || 0} icon={<ShoppingBag className="w-3.5 h-3.5" />} />
          <Row label="На общую сумму" value={`${(customer.total_spent || 0).toLocaleString("ru-RU")} ₽`} />
        </div>
      )}
    </div>
  );
}

function Row({ label, value, icon }) {
  return (
    <div className="flex items-center justify-between border-b border-zinc-800/60 pb-2">
      <span className="text-[11px] font-mono text-zinc-500 uppercase tracking-wider flex items-center gap-1.5">
        {icon} {label}
      </span>
      <span className="text-sm text-white">{value}</span>
    </div>
  );
}
